import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const useAzureAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for anonymous token in localStorage
  const getStoredAnonymousAuth = useCallback(() => {
    try {
      const stored = localStorage.getItem('anonymousAuth');
      if (stored) {
        const auth = JSON.parse(stored);
        // Check if token is expired
        if (new Date(auth.expiresAt) > new Date()) {
          return auth;
        } else {
          // Clean up expired token
          localStorage.removeItem('anonymousAuth');
        }
      }
    } catch (error) {
      console.error('Error parsing stored auth:', error);
      localStorage.removeItem('anonymousAuth');
    }
    return null;
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      // First check for anonymous token authentication
      const anonymousAuth = getStoredAnonymousAuth();
      if (anonymousAuth) {
        setIsAuthenticated(true);
        setUser(anonymousAuth.user);
        setLoading(false);
        return;
      }

      // Then check for OAuth authentication
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/getAuthStatus'
        : '/api/getAuthStatus';

      const response = await axios.get(apiUrl, {
        withCredentials: true
      });

      setIsAuthenticated(response.data.isAuthenticated);
      setUser(response.data.user);
    } catch (error) {
      console.error('Error checking authentication status:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [getStoredAnonymousAuth]);

  const validateAnonymousToken = useCallback(async (token, email) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/validateAnonymousToken'
        : '/api/validateAnonymousToken';

      const response = await axios.post(apiUrl, {
        token,
        email
      });

      if (response.data.isAuthenticated) {
        // Store authentication in localStorage
        const authData = {
          user: response.data.user,
          token: response.data.token,
          expiresAt: response.data.expiresAt
        };
        localStorage.setItem('anonymousAuth', JSON.stringify(authData));
        
        setIsAuthenticated(true);
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Error validating anonymous token:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erreur de validation du token' 
      };
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const logout = useCallback(() => {
    const anonymousAuth = getStoredAnonymousAuth();
    if (anonymousAuth) {
      // Anonymous user logout
      localStorage.removeItem('anonymousAuth');
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = '/';
    } else {
      // OAuth user logout
      window.location.href = '/.auth/logout';
    }
  }, [getStoredAnonymousAuth]);

  return {
    isAuthenticated,
    user,
    loading,
    checkAuthStatus,
    validateAnonymousToken,
    logout
  };
};